/**
 * Operator CLI handoff for Kryspace skill manifest / deploy-drift checks.
 * Admin does not call Kryspace from the browser (no Backend proxy yet).
 */

export function buildVerifySkillManifestsCommand(skillBundles: string[]): string {
  const flags = uniqueSkillFlags(skillBundles);
  return `kryspace verify-skill-manifests${flags} --json`;
}

export function buildSkillDeployDriftCommand(skillBundles: string[]): string {
  const flags = uniqueSkillFlags(skillBundles);
  return `kryspace skill-deploy-drift${flags}`;
}

function uniqueSkillFlags(skillBundles: string[]): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const raw of skillBundles) {
    const slug = typeof raw === 'string' ? raw.trim() : '';
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    parts.push(` --skill ${shellSingleQuote(slug)}`);
  }
  return parts.join('');
}

/** Minimal quoting for PowerShell / bash-safe single-quoted tokens. */
export function shellSingleQuote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

export type SkillVerifyReceiptKind = 'manifest' | 'deploy_drift' | 'unknown';

export interface SkillVerifySkillRow {
  skill: string;
  status?: string;
  deployDrift?: boolean;
  driftReasons?: string[];
  onlyInManifest?: string[];
  onlyOnDisk?: string[];
  staleFiles?: string[];
  deployState?: string;
  currentVersion?: string;
}

export interface SkillVerifyReceiptSummary {
  kind: SkillVerifyReceiptKind;
  ok?: boolean;
  skillsScanned?: number;
  skillsWithDrift?: number;
  skills: SkillVerifySkillRow[];
  verificationRunAt?: string;
  parseError?: string;
}

export function parseSkillVerifyReceipt(raw: string): SkillVerifyReceiptSummary {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { kind: 'unknown', skills: [], parseError: 'Paste a JSON receipt from the CLI.' };
  }

  let data: unknown;
  try {
    data = JSON.parse(trimmed);
  } catch {
    return { kind: 'unknown', skills: [], parseError: 'Invalid JSON — paste full CLI stdout.' };
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { kind: 'unknown', skills: [], parseError: 'Expected a JSON object receipt.' };
  }

  const obj = data as Record<string, unknown>;
  const kind = detectReceiptKind(obj);
  const skills = Array.isArray(obj.skills) ? obj.skills : [];
  const rows: SkillVerifySkillRow[] = [];

  for (const entry of skills) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const row = entry as Record<string, unknown>;
    const skill = typeof row.skill === 'string' ? row.skill : '';
    if (!skill) continue;

    const registry = Array.isArray(row.registry_rows) ? row.registry_rows : [];
    const firstReg =
      registry.find((r) => r && typeof r === 'object' && !Array.isArray(r)) as
        | Record<string, unknown>
        | undefined;

    rows.push({
      skill,
      status: typeof row.status === 'string' ? row.status : typeof row.manifest_status === 'string' ? row.manifest_status : undefined,
      deployDrift: typeof row.deploy_drift === 'boolean' ? row.deploy_drift : undefined,
      driftReasons: stringArray(row.drift_reasons),
      onlyInManifest: stringArray(row.only_in_manifest),
      onlyOnDisk: stringArray(row.only_on_disk),
      staleFiles: stringArray(row.stale_files),
      deployState: typeof firstReg?.deploy_state === 'string' ? firstReg.deploy_state : undefined,
      currentVersion:
        typeof firstReg?.current_version === 'string' ? firstReg.current_version : undefined,
    });
  }

  const receipt =
    obj.receipt && typeof obj.receipt === 'object' && !Array.isArray(obj.receipt)
      ? (obj.receipt as Record<string, unknown>)
      : obj.manifest_receipt &&
          typeof obj.manifest_receipt === 'object' &&
          !Array.isArray(obj.manifest_receipt)
        ? (obj.manifest_receipt as Record<string, unknown>)
        : null;

  const summary =
    receipt?.summary && typeof receipt.summary === 'object' && !Array.isArray(receipt.summary)
      ? (receipt.summary as Record<string, unknown>)
      : null;

  return {
    kind,
    ok: typeof obj.ok === 'boolean' ? obj.ok : undefined,
    skillsScanned:
      typeof obj.skills_scanned === 'number'
        ? obj.skills_scanned
        : typeof summary?.skills_checked === 'number'
          ? summary.skills_checked
          : undefined,
    skillsWithDrift:
      typeof obj.skills_with_drift === 'number'
        ? obj.skills_with_drift
        : typeof obj.skills_with_deploy_drift === 'number'
          ? obj.skills_with_deploy_drift
          : typeof summary?.skills_with_drift === 'number'
            ? summary.skills_with_drift
            : undefined,
    skills: rows,
    verificationRunAt:
      typeof receipt?.verification_run_at === 'string'
        ? receipt.verification_run_at
        : typeof obj.generated_at === 'string'
          ? obj.generated_at
          : undefined,
  };
}

function detectReceiptKind(obj: Record<string, unknown>): SkillVerifyReceiptKind {
  if (
    typeof obj.skills_with_deploy_drift === 'number' ||
    obj.tool_version != null ||
    (Array.isArray(obj.skills) &&
      obj.skills.some(
        (s) =>
          s &&
          typeof s === 'object' &&
          !Array.isArray(s) &&
          typeof (s as Record<string, unknown>).deploy_drift === 'boolean'
      ))
  ) {
    return 'deploy_drift';
  }
  if (
    typeof obj.ok === 'boolean' ||
    typeof obj.skills_with_drift === 'number' ||
    (obj.receipt && typeof obj.receipt === 'object')
  ) {
    return 'manifest';
  }
  return 'unknown';
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
  return out.length ? out : [];
}
